'use client';

import React, { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Lock, Mail, Loader2, UserCheck } from 'lucide-react';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect') || '/account';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Dështoi hyrja në llogari.');
      }

      if (data.user?.roles?.some((r: string) => r !== 'CUSTOMER')) {
        router.push('/admin');
      } else {
        router.push(redirect);
      }
      router.refresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = (demoEmail: string, demoPass: string) => {
    setEmail(demoEmail);
    setPassword(demoPass);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">Email</label>
          <div className="relative">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="emri@shembull.al"
              className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">Fjalëkalimi</label>
          <div className="relative">
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-slate-900 dark:bg-sky-600 hover:bg-sky-600 dark:hover:bg-sky-500 text-white font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Duke verifikuar...
            </>
          ) : (
            'Hyr në Llogari'
          )}
        </button>

        <div className="text-center pt-2 text-xs text-slate-500 dark:text-slate-400">
          Nuk keni ende një llogari?{' '}
          <Link href="/register" className="text-sky-600 dark:text-sky-400 hover:underline font-bold">
            Regjistrohu këtu
          </Link>
        </div>
      </form>

      {/* Quick Demo Credentials Box */}
      <div className="bg-slate-100/80 dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
          <UserCheck className="w-4 h-4 text-sky-600 dark:text-sky-400" />
          Llogari Demonstruese (Kliko për plotësim të shpejtë):
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => handleQuickLogin('customer@gmail.com', 'Customer123!')}
            className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-sky-500 text-left font-medium text-slate-800 dark:text-slate-200 transition-colors cursor-pointer"
          >
            <strong className="block text-sky-600 dark:text-sky-400">Klient Blerës</strong>
            customer@gmail.com
          </button>

          <button
            type="button"
            onClick={() => handleQuickLogin('superadmin@dyrma.al', 'Admin123!')}
            className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-sky-500 text-left font-medium text-slate-800 dark:text-slate-200 transition-colors cursor-pointer"
          >
            <strong className="block text-indigo-600 dark:text-indigo-400">Super Admin</strong>
            superadmin@dyrma.al
          </button>

          <button
            type="button"
            onClick={() => handleQuickLogin('manager@dyrma.al', 'Admin123!')}
            className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-sky-500 text-left font-medium text-slate-800 dark:text-slate-200 transition-colors cursor-pointer"
          >
            <strong className="block text-emerald-600 dark:text-emerald-400">Menaxher Financë</strong>
            manager@dyrma.al
          </button>

          <button
            type="button"
            onClick={() => handleQuickLogin('tech@dyrma.al', 'Admin123!')}
            className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-sky-500 text-left font-medium text-slate-800 dark:text-slate-200 transition-colors cursor-pointer"
          >
            <strong className="block text-amber-600 dark:text-amber-400">Teknik Montimi</strong>
            tech@dyrma.al
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="max-w-md mx-auto px-4 py-12 w-full space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 rounded-2xl bg-slate-900 dark:bg-slate-800 text-sky-400 flex items-center justify-center mx-auto shadow-md border border-slate-800 dark:border-slate-700">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 dark:text-white">Hyrje në Llogari</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Identifikohuni për të parë porositë, preventivat dhe shërbimet tuaja.
        </p>
      </div>

      <Suspense fallback={<div className="text-center py-8 text-xs text-slate-400">Duke ngarkuar formularin...</div>}>
        <LoginForm />
      </Suspense>
    </div>
  );
}
